/**
 * feat-302 — the audience editor: a non-modal popover under the chip at `sm+`,
 * the feat-298 modal bottom sheet below `sm` (design NOTES §2 "Popover", §5, §6).
 *
 * Saves the WHOLE audience object (ADR §10.3). Nothing here decides who passes —
 * the "Who will see it" line only restates the draft in words.
 */
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { CircleAlert, Info, Loader2, Search, X } from 'lucide-react';
import {
  Audience,
  AudienceMode,
  AudienceOptions,
} from '../../../../services/quizContentAdminService';
import { ERRBTN, FOCUS } from '../InlineEditable';
import { AUDIENCE_COPY as COPY, AudienceLevel } from './audienceCopy';
import {
  audienceFragment,
  dimensionSummary,
  invalidDimensions,
  invalidReason,
  previewSentence,
  sameAudience,
  wireAudience,
} from './audienceFormat';
import { readyOptions, TrackRef, useAudience } from './audienceContext';

// --- class constants, verbatim from design NOTES §3 -------------------------------
const POPOVER_BASE =
  'absolute right-0 z-40 w-[22rem] rounded-2xl bg-white shadow-xl ring-1 ring-black/10 text-left';
/** NOTES §3 `POPOVER`; `top-full mt-2` swaps for `bottom-full mb-2` when flipped. */
export const POPOVER = `${POPOVER_BASE} top-full mt-2`;
const POPOVER_UP = `${POPOVER_BASE} bottom-full mb-2`;
export const SHEET =
  'fixed inset-x-0 bottom-0 z-[60] max-h-[85dvh] overflow-y-auto rounded-t-3xl bg-white shadow-2xl text-left';
const SEG = 'inline-flex rounded-full bg-cream-dark p-0.5';
const SEG_OPT =
  'h-7 px-3 rounded-full text-xs font-semibold text-inkMuted hover:text-ink inline-flex items-center cursor-pointer';
const SEG_ON = 'bg-white text-ink shadow-sm ring-1 ring-black/10';
const LIST_CORE =
  'mt-2 max-h-44 overflow-y-auto overscroll-contain rounded-xl divide-y divide-softBorder';
export const LIST = `${LIST_CORE} ring-1 ring-black/10`;
export const LIST_INVALID = `${LIST_CORE} ring-2 ring-attention-line`;
const ROW = 'flex items-center gap-2.5 px-3 h-9 text-sm text-ink cursor-pointer hover:bg-cream';
const BTN_SAVE = `h-8 px-4 rounded-full bg-white ring-1 ring-black/10 text-sm font-semibold text-ink hover:bg-cream-dark disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white ${FOCUS}`;
const BTN_CANCEL = `h-8 px-3 rounded-full text-sm font-semibold text-inkMuted hover:text-ink hover:bg-cream-dark ${FOCUS}`;
const INVALID_MSG = 'flex items-start gap-1.5 text-xs leading-snug text-attention-on';
const ERRBOX =
  'rounded-lg bg-danger-surface border border-danger-line/40 px-3 py-2 text-xs leading-snug text-danger-text';

/** Below this many options the list needs no filter box (NOTES §5). */
const FILTER_THRESHOLD = 8;

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * feat-302 D3 — space to keep clear at the bottom of the viewport when deciding
 * whether the popover opens downward. Between 640 and 767 px the floating
 * language pill (bottom-right) would otherwise sit over the popover's footer and
 * its Save button. Clearing it is preferred over a z-index fight.
 */
export const POPOVER_BOTTOM_CLEARANCE = 80;

/** Open upward when the space below the chip cannot hold the popover plus the
 *  bottom clearance. Pure, so the decision is tested directly. */
export function shouldFlipUp(anchorBottom: number, popoverHeight: number, viewportHeight: number): boolean {
  return viewportHeight - anchorBottom < popoverHeight + POPOVER_BOTTOM_CLEARANCE;
}

const isSheetViewport = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 639px)').matches;

// ------------------------------------------------------------ segments

const Segments: React.FC<{
  legend: string;
  name: string;
  value: AudienceMode;
  disabled: boolean;
  onChange: (m: AudienceMode) => void;
}> = ({ legend, name, value, disabled, onChange }) => {
  const opts: Array<[AudienceMode, string]> = [
    ['everyone', COPY.modeEveryone],
    ['only', COPY.modeOnly],
    ['except', COPY.modeExcept],
  ];
  return (
    <div className={`${SEG}${disabled ? ' opacity-50' : ''}`} role="radiogroup" aria-label={legend}>
      {opts.map(([v, label]) => (
        <label
          key={v}
          className={`${SEG_OPT} ${value === v ? SEG_ON : ''} has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-coral/40`}
        >
          <input
            type="radio"
            className="sr-only"
            name={name}
            value={v}
            checked={value === v}
            disabled={disabled}
            onChange={() => onChange(v)}
          />
          {label}
        </label>
      ))}
    </div>
  );
};

// ------------------------------------------------------------ option list

const OptionList: React.FC<{
  caption: string;
  filterLabel: string;
  options: Array<{ id: string; name: string }>;
  selected: string[];
  invalid: boolean;
  disabled: boolean;
  onToggle: (id: string) => void;
}> = ({ caption, filterLabel, options, selected, invalid, disabled, onToggle }) => {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  return (
    <>
      <p className="mt-2 text-xs font-semibold text-ink">{caption}</p>
      {options.length > FILTER_THRESHOLD && (
        <div className="relative mt-2">
          <Search className="absolute left-2.5 top-2 w-4 h-4 text-inkMuted" aria-hidden />
          <input
            type="search"
            value={query}
            disabled={disabled}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={filterLabel}
            placeholder={filterLabel}
            className={`h-8 w-full rounded-lg ring-1 ring-black/10 pl-8 pr-2.5 text-sm placeholder:text-inkMuted ${FOCUS}`}
          />
        </div>
      )}
      <div className={invalid ? LIST_INVALID : LIST} aria-disabled={disabled || undefined}>
        {shown.map((o) => (
          <label key={o.id} className={`${ROW}${disabled ? ' opacity-60' : ''}`}>
            <input
              type="checkbox"
              className="w-4 h-4 accent-coral"
              checked={selected.includes(o.id)}
              disabled={disabled}
              onChange={() => onToggle(o.id)}
            />
            <span className="truncate">{o.name}</span>
          </label>
        ))}
      </div>
    </>
  );
};

// ------------------------------------------------------------ Track note

const TrackNote: React.FC<{
  level: AudienceLevel;
  tracks: TrackRef[];
  options: AudienceOptions | null;
}> = ({ level, tracks, options }) => {
  let body: React.ReactNode = null;
  if (level === 'track') {
    body = COPY.trackPopoverNote;
  } else if (tracks.length === 1) {
    body = (
      <>
        {COPY.trackNoteLead} <b>{tracks[0].title}</b> {COPY.trackNoteShownTo}{' '}
        {audienceFragment(tracks[0].audience, options)}.
      </>
    );
  } else if (tracks.length > 1) {
    body = (
      <>
        {COPY.trackNoteManyLead}{' '}
        {tracks.map((t, i) => (
          <React.Fragment key={t.id}>
            {i > 0 && ', '}
            <b>{t.title}</b> ({audienceFragment(t.audience, options)})
          </React.Fragment>
        ))}
        .
      </>
    );
  }
  if (!body) return null;
  return (
    <p
      className="mx-4 mt-3 flex items-start gap-1.5 text-[11px] leading-4 text-inkMuted"
      data-testid="audience-track-note"
    >
      <Info className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden />
      <span>{body}</span>
    </p>
  );
};

// ------------------------------------------------------------ the editor

export interface AudienceEditorProps {
  level: AudienceLevel;
  id: string;
  name: string;
  /** The SAVED audience — the draft starts here; a locked dimension sends it. */
  audience: Audience;
  anchorRef: React.RefObject<HTMLElement | null>;
  onCancel: () => void;
  onSaved: () => void;
}

const cloneAudience = (a: Audience): Audience => ({
  locations: { mode: a.locations.mode, ids: [...a.locations.ids] },
  roles: { mode: a.roles.mode, keys: [...a.roles.keys] },
});

const AudienceEditor: React.FC<AudienceEditorProps> = ({
  level,
  id,
  name,
  audience,
  anchorRef,
  onCancel,
  onSaved,
}) => {
  const api = useAudience();
  const uid = useId();
  const titleId = `${uid}-title`;
  const reasonId = `${uid}-reason`;
  const [sheet] = useState(isSheetViewport);
  const [flipped, setFlipped] = useState(false);
  const [draft, setDraft] = useState<Audience>(() => cloneAudience(audience));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const mountedRef = useRef(true);
  const savingRef = useRef(false);
  savingRef.current = saving;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Measure ONCE on open: flip upward when the space below the chip is less than
  // the popover's height. No live re-positioning (NOTES §2).
  useLayoutEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
    if (sheet) return undefined;
    const anchor = anchorRef.current?.getBoundingClientRect();
    const height = rootRef.current?.getBoundingClientRect().height ?? 0;
    if (anchor && height > 0 && shouldFlipUp(anchor.bottom, height, window.innerHeight)) setFlipped(true);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The sheet is modal: lock body scroll while it is up.
  useLayoutEffect(() => {
    if (!sheet) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheet]);

  // The popover is non-modal: a mousedown outside it (and outside its chip)
  // discards the draft, like Esc. Ignored while a save is in flight.
  useEffect(() => {
    if (sheet) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      if (savingRef.current) return;
      onCancelRef.current();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [sheet, anchorRef]);

  if (!api) return null;
  const optionsState = api.options;
  const options = readyOptions(optionsState);
  const locLocked = !options;
  const roleLocked = !options;
  const noRoles = !!options && options.roles.length === 0;
  const invalid = invalidDimensions(draft);
  const reason = invalidReason(draft);
  const tracks = level === 'lesson' ? api.tracksForLesson(id) : [];

  const setLocMode = (mode: AudienceMode) =>
    setDraft((d) => ({ ...d, locations: { ...d.locations, mode } }));
  const setRoleMode = (mode: AudienceMode) =>
    setDraft((d) => ({ ...d, roles: { ...d.roles, mode } }));
  const toggleLoc = (locId: string) =>
    setDraft((d) => ({
      ...d,
      locations: {
        ...d.locations,
        ids: d.locations.ids.includes(locId)
          ? d.locations.ids.filter((x) => x !== locId)
          : [...d.locations.ids, locId],
      },
    }));
  const toggleRole = (key: string) =>
    setDraft((d) => ({
      ...d,
      roles: {
        ...d.roles,
        keys: d.roles.keys.includes(key) ? d.roles.keys.filter((x) => x !== key) : [...d.roles.keys, key],
      },
    }));

  const submit = async () => {
    if (saving || invalid.any) return;
    // No change → close without a request (no no-op audit row, NOTES §6.2).
    if (sameAudience(draft, audience)) {
      onCancel();
      return;
    }
    setSaving(true);
    setError(false);
    try {
      await api.save(level, id, wireAudience(draft));
      if (!mountedRef.current) return;
      onSaved();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[QuizContentReview] audience save failed:', String(e));
      if (mountedRef.current) setError(true);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      // Esc does nothing until a save settles (NOTES §6.3).
      if (!saving) onCancel();
      return;
    }
    if (e.key !== 'Tab' || !rootRef.current) return;
    const items = Array.from(rootRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === headingRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const loadFailed = optionsState.status === 'failed';
  const retry = (
    <button type="button" className={ERRBTN} onClick={api.retryOptions}>
      {COPY.tryAgain}
    </button>
  );

  const body = (
    <>
      <div className="flex items-start gap-2 px-4 pt-4 pb-3 border-b border-softBorder">
        <div className="min-w-0 flex-1">
          <h3
            ref={headingRef}
            id={titleId}
            tabIndex={-1}
            className="font-display text-lg font-semibold text-ink leading-tight focus:outline-none"
          >
            {COPY.title(level)}
          </h3>
          <p className="text-xs text-inkMuted truncate mt-0.5" title={name}>
            {name}
          </p>
        </div>
        <button
          type="button"
          aria-label={COPY.close}
          disabled={saving}
          onClick={onCancel}
          className={`shrink-0 -mr-1 -mt-1 w-8 h-8 grid place-items-center rounded-full text-inkMuted hover:text-ink hover:bg-cream-dark ${FOCUS}`}
        >
          <X className="w-4 h-4" aria-hidden />
        </button>
      </div>

      {/* Locations */}
      <fieldset className="px-4 pt-3" disabled={saving}>
        <div className="flex items-center justify-between gap-3">
          <legend className="text-sm font-semibold text-ink float-left">{COPY.locations}</legend>
          <Segments
            legend={COPY.locations}
            name={`${uid}-loc`}
            value={draft.locations.mode}
            disabled={locLocked || saving}
            onChange={setLocMode}
          />
        </div>
        {locLocked ? (
          loadFailed && (
            <p className="mt-2 text-xs text-danger-text flex items-start gap-1.5" role="alert">
              <CircleAlert className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden />
              <span>
                {COPY.locationsLoadFailed(dimensionSummary(audience, 'locations', null))} {retry}
              </span>
            </p>
          )
        ) : (
          draft.locations.mode !== 'everyone' && (
            <OptionList
              caption={
                draft.locations.mode === 'only'
                  ? COPY.captionLocationsOnly
                  : COPY.captionLocationsExcept
              }
              filterLabel={COPY.findLocation}
              options={options!.locations}
              selected={draft.locations.ids}
              invalid={invalid.locations}
              disabled={saving}
              onToggle={toggleLoc}
            />
          )
        )}
        <p className="mt-1.5 text-[11px] leading-4 text-inkMuted">{COPY.multiLocationNote}</p>
      </fieldset>

      {/* Roles */}
      <fieldset className="px-4 pt-4" disabled={saving}>
        <div className="flex items-center justify-between gap-3">
          <legend className="text-sm font-semibold text-ink float-left">{COPY.roles}</legend>
          <Segments
            legend={COPY.roles}
            name={`${uid}-role`}
            value={draft.roles.mode}
            disabled={roleLocked || noRoles || saving}
            onChange={setRoleMode}
          />
        </div>
        {roleLocked ? (
          loadFailed && (
            <p className="mt-2 text-xs text-danger-text flex items-start gap-1.5" role="alert">
              <CircleAlert className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden />
              <span>
                {COPY.rolesLoadFailed(dimensionSummary(audience, 'roles', null))} {retry}
              </span>
            </p>
          )
        ) : noRoles ? (
          <p className="mt-2 text-xs text-inkMuted">{COPY.noRoles}</p>
        ) : (
          draft.roles.mode !== 'everyone' && (
            <OptionList
              caption={draft.roles.mode === 'only' ? COPY.captionRolesOnly : COPY.captionRolesExcept}
              filterLabel={COPY.findRole}
              options={options!.roles.map((r) => ({ id: r.role_key, name: r.display_name }))}
              selected={draft.roles.keys}
              invalid={invalid.roles}
              disabled={saving}
              onToggle={toggleRole}
            />
          )
        )}
      </fieldset>

      <TrackNote level={level} tracks={tracks} options={options} />

      <div className="mx-4 mt-4 rounded-xl bg-cream px-3 py-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted">
          {COPY.previewLabel}
        </p>
        <p className="text-sm text-ink mt-0.5" aria-live="polite" data-testid="audience-preview">
          {invalid.any ? (
            <span className="text-inkMuted">{COPY.previewInvalid}</span>
          ) : (
            previewSentence(draft, options)
          )}
        </p>
      </div>

      {error && (
        <div className={`mx-4 mt-3 ${ERRBOX}`} role="alert">
          {COPY.saveError(level)}{' '}
          <button type="button" className={ERRBTN} onClick={submit}>
            {COPY.tryAgain}
          </button>
        </div>
      )}

      <div
        className={`px-4 pt-3 pb-4${
          sheet ? ' sticky bottom-0 bg-white border-t border-softBorder mt-3' : ''
        }`}
      >
        {reason && (
          <p id={reasonId} className={`${INVALID_MSG} mb-2`}>
            <CircleAlert className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden />
            <span>{reason}</span>
          </p>
        )}
        <div className="flex items-center gap-2">
          <p className="text-[11px] leading-4 text-inkMuted mr-auto">{COPY.footer}</p>
          <button type="button" className={BTN_CANCEL} disabled={saving} onClick={onCancel}>
            {COPY.cancel}
          </button>
          <button
            type="button"
            className={BTN_SAVE}
            disabled={invalid.any || saving}
            aria-describedby={reason ? reasonId : undefined}
            onClick={submit}
          >
            {saving ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
                {COPY.saving}
              </span>
            ) : (
              COPY.save
            )}
          </button>
        </div>
      </div>
    </>
  );

  if (sheet) {
    return (
      <>
        <div
          aria-hidden
          data-testid="audience-sheet-backdrop"
          className="fixed inset-0 z-[60] bg-black/40"
          onClick={() => {
            if (!saving) onCancel();
          }}
        />
        <div
          ref={rootRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onKeyDown={onKeyDown}
          className={SHEET}
        >
          <div aria-hidden className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-black/15" />
          {body}
        </div>
      </>
    );
  }
  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      className={flipped ? POPOVER_UP : POPOVER}
    >
      {body}
    </div>
  );
};

export default AudienceEditor;
